import { apiFetch } from "./client";

/**
 * "Finansije dobavljača" (Supplier Finance) — mirrors api/finance.ts on the
 * opposite ledger direction: balances here represent what BANKOM owes the
 * supplier, not what a customer owes Bankom. Shapes are kept structurally
 * identical to the customer finance types so `FinanceDetail` (finance/FinanceDetail.tsx)
 * can be reused as-is to render the account/aging/invoices/payments/turnover.
 */

export type InvoiceStatus = "OPEN" | "PARTIALLY_PAID" | "PAID" | "CANCELLED";

/** `kind` tells the screen whether a combined view exists for this company. */
export type CompanyRef = { id: string; name: string; country: string | null; kind: string | null };

/** Same shape as the customer side: all accounts in euro, the split and the day of the rate. */
export type AccountView = {
  currency: string;
  balanceTotal: number;
  overdueTotal: number;
  creditLimit: number | null;
  creditAvailable: number | null;
  byCurrency: { currency: string; balance: number; overdue: number }[];
  rateDate: string | null;
};

export type AgingView = {
  /**
   * Valuta ovih kofa — razlaganje ide PO VALUTI i ne preračunava se.
   *
   * Dok je bila jedna traka, firma sa 711,08 EUR i 40.321,08 USD dobijala je kofu od 41.032,16
   * označenu dolarom, jer je valuta uzeta sa „glavnog" naloga. Ista strana je tada sama sebe
   * obarala: zbir kofa 41.033,78 prema KPI saldu 40.321,08.
   */
  currency: string;
  notDue: number;
  d7: number;
  d30: number;
  d60: number;
  d120: number;
  d120Plus: number;
  /** Prenos iz ranijih godina: dospelo, starost nepoznata. */
  carried: number;
};

export type InvoiceView = {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  grossAmount: number;
  paidAmount: number;
  openAmount: number;
  status: InvoiceStatus;
  overdue: boolean;
  orderReference: string | null;
  /** Predmet dokumenta iz ERP-a; isto kao na strani kupca, jer je i ekran isti. */
  description: string | null;
  /** Vrsta dokumenta iz ERP-a, doslovno. */
  documentType: string | null;
};

export type PaymentView = {
  id: string;
  paymentDate: string;
  currency: string;
  amount: number;
  method: string | null;
  reference: string | null;
  invoiceNumber: string | null;
};

export type TurnoverBin = { label: string; from: string; to: string; invoicedEur: number };

export type SupplierFinanceDetail = {
  company: CompanyRef;
  account: AccountView;
  /** Starosno razlaganje PO VALUTI — jedan red po valuti u kojoj firma ima otvoreno. */
  aging: AgingView[];
  invoices: InvoiceView[];
  payments: PaymentView[];
  turnover: { periods: TurnoverBin[] };
  from: string;
  to: string;
  /** The server's verdict on whether the combined buyer-and-supplier view opens for the caller. */
  bothSidesVisible: boolean;
  /**
   * The day overdue and the aging are counted on: the day the incoming invoices reach, or today if
   * that comes first. The screen names it beside both.
   */
  asOf?: string | null;
};

export type CompanyRow = {
  companyId: string;
  name: string;
  country: string | null;
  /** Saldo preračunat u evre. */
  balanceEur: number;
  overdueEur: number;
  /** Saldo po valuti, kako stoji na računima. */
  balanceByCurrency: Record<string, number>;
  creditLimit: number | null;
  turnoverEur: number;
  /** Nedospelo i kašnjenje 1–7, 8–30, 31–60, 61–120 i preko 120 dana — isti raspon kao kod kupaca. */
  notDueEur: number;
  d7Eur: number;
  d30Eur: number;
  d60Eur: number;
  d120Eur: number;
  d120PlusEur: number;
  /** Prenos iz ranijih godina: dospelo, starost nepoznata. */
  carriedEur: number;
  lastPaymentDate: string | null;
};

export type SupplierFinanceOverview = {
  totals: {
    balanceEur: number;
    overdueEur: number;
    /** Purchased in the period, VAT included: invoices only, without the opening balance. */
    turnoverEur: number;
    /** The opening balance posted in the period, left out of `turnoverEur`. */
    carriedPostedEur: number;
    /** Other postings in the period: reposts, tax calculations, ledger orders. */
    otherPostedEur: number;
    companyCount: number;
    companiesWithOverdue: number;
    /** How many amounts were left out of the euro totals for want of a rate. */
    unconvertedCount: number;
    notDueEur: number;
    d7Eur: number;
    d30Eur: number;
    d60Eur: number;
    d120Eur: number;
    d120PlusEur: number;
  /** Prenos iz ranijih godina: dospelo, starost nepoznata. */
  carriedEur: number;
  };
  /** Tržišta nema: OTKUP nema taj opseg (za razliku od prodaje). Sve ostalo ima. */
  filters: {
    from: string; to: string; q: string | null;
    country: string | null; currency: string | null; bucket: string | null; sort: string | null;
  };
  /** Države koje se pojavljuju među dobavljačima u opsegu; pune padajući filter. */
  availableCountries: string[];
  /** Valute u kojima se dobavljačima stvarno duguje; pune padajući filter. */
  availableCurrencies: string[];
  companies: CompanyRow[];
  page: number;
  size: number;
  totalElements: number;
  /** The day overdue and the aging are counted on, as on the card of a supplier. */
  asOf?: string | null;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

/** Jedna stavka računa: šta je kupljeno, koliko, po kojoj ceni i u koju šaržu. */
export type InvoiceLine = {
  productName: string | null;
  erpProductId: string | null;
  unit: string | null;
  quantity: number | null;
  unitPrice: number | null;
  netAmount: number | null;
  lotDesignation: string | null;
  goodsLotId: string | null;
};

/** Jedan prijem koji račun pokriva; `deliveryId` je prazan kad broj nije razrešen u isporuku. */
export type InvoiceDelivery = {
  reference: string;
  deliveryId: string | null;
  receivedOn: string | null;
  netKg: number | null;
  lotDesignation: string | null;
};

export type InvoiceContent = { lines: InvoiceLine[]; deliveries: InvoiceDelivery[] };

export type Period = {
  from?: string; to?: string; q?: string;
  country?: string; currency?: string; bucket?: string; sort?: string;
};

export type CombinedLedger = {
  company: CompanyRef;
  customerSide: AccountView;
  supplierSide: AccountView;
  /** Null when the two sides sit in currencies with no rate on file. */
  netPosition: number | null;
  currency: string;
  /** The day of the oldest rate behind the net position; null when both sides are in euro. */
  rateDate: string | null;
  /** The day overdue on the customer side is counted on: the day the outgoing invoices reach. */
  customerAsOf?: string | null;
  /** The day overdue on the co-operative side is counted on: the day the incoming invoices reach. */
  supplierAsOf?: string | null;
};

export const supplierFinanceApi = {
  me: (p: Period = {}) => apiFetch<SupplierFinanceDetail>(`/supplier-finance/me${q(p)}`),
  /**
   * Pregled po stranama. Dobavljača je 149, a server podrazumevano vraća 50 — dok se strana nije
   * tražila, ekran je pokazivao prvih pedeset i to nije govorio.
   */
  overview: (p: Period = {}, page = 0, size = 50) =>
    apiFetch<SupplierFinanceOverview>(
      `/supplier-finance/overview${q({ ...p, page: String(page), size: String(size) })}`,
    ),
  company: (id: string, p: Period = {}) =>
    apiFetch<SupplierFinanceDetail>(`/supplier-finance/companies/${id}${q(p)}`),
  /**
   * Šta je račun kupio i koji prijem pokriva.
   *
   * Traži se tek kad se račun otvori, a ne uz spisak: spisak od pedeset računa inače bi nosio svaku
   * stavku svakog od njih, a stavke se gledaju jedan račun po jedan.
   */
  invoiceContent: (invoiceId: string) =>
    apiFetch<InvoiceContent>(`/supplier-finance/invoices/${invoiceId}/content`),
  combined: (companyId: string) =>
    apiFetch<CombinedLedger>(`/supplier-finance/companies/${companyId}/combined`),
};
